import type { GetArtifactResp } from "@/types/artifacts.ts";
import type { ClaimedArtifactResp, SharedArtifactPayload } from "@/types/sharedArtifact.ts";

/**
 * Anything a surface might name. The redacted shapes a share serves drop
 * `messageId`, so they are not a `GetArtifactResp` — but naming an artifact never
 * touches the fields that were redacted.
 */
type NameableArtifact = GetArtifactResp | ClaimedArtifactResp | SharedArtifactPayload;

/**
 * Generic fallback label — the artifact's own type title (e.g. "Email",
 * "Document"). `artifactTypeTitle` only exists on heavy artifacts, so the `in`
 * guard keeps this total across the full (heavy | light) union; the raw
 * `artifactType` is the last resort so a surface never renders a blank label.
 */
const typeTitle = (artifact: NameableArtifact): string =>
  ("artifactTypeTitle" in artifact && artifact.artifactTypeTitle) || artifact.artifactType;

/**
 * The human label for an artifact: the most title-like field for its type
 * (email subject, doc title, file name, …), falling back to the generic type
 * title. Shared by every surface that names an artifact — in-thread chips,
 * expanded headers (lightbox, canvas, native detail screen), and Artifact
 * Library rows — so a chip and the screen it opens can't disagree about what
 * the same artifact is called.
 *
 * A type with no case here degrades to the type title rather than needing a
 * change, so adding an artifact type never breaks a caller.
 */
export const artifactDisplayTitle = (artifact: NameableArtifact): string => {
  switch (artifact.artifactType) {
    case "emailDraft":
    case "schedulingKickoff":
      return artifact.subject || typeTitle(artifact);
    case "invitation":
      return artifact.summary || typeTitle(artifact);
    case "document":
    case "applet":
    case "threadArtifact":
      return artifact.title || typeTitle(artifact);
    case "textMessageDraft":
    case "directMessageDraft":
      return artifact.body || typeTitle(artifact);
    case "phoneCallDraft":
      return artifact.recipientName || typeTitle(artifact);
    // The skill's name, which is also its identity and its slash command — and
    // the one thing a collapsed chip has room to say. Without this the chip
    // reads "Skill", which does not distinguish the draft the user is being
    // asked to approve from any other.
    case "skillArtifact":
      return artifact.name || typeTitle(artifact);
    case "file":
      return artifact.filename || typeTitle(artifact);
    case "externalFileLink":
      return artifact.fileName || typeTitle(artifact);
    default:
      return typeTitle(artifact);
  }
};

/**
 * The label for a surface that expands an artifact — lightbox, canvas card,
 * native detail screen, the native header the webview bridges a title to.
 *
 * A read, not a composition: the server decides whether a type has something
 * longer to say than its chip label, and says it. Today only a picker does
 * ("Availability: which of these mean you're busy?"), and every other type
 * falls through to the ordinary display title.
 *
 * Deliberately not used by the in-thread chip or a Library row. Those render
 * one truncating line, where a full question clips mid-sentence.
 */
export const artifactExpandedTitle = (artifact: NameableArtifact): string =>
  ("expandedTitle" in artifact && artifact.expandedTitle) || artifactDisplayTitle(artifact);
